import { TaxonomyNode, TaxonomyNodeApi } from "types";
import csTagNodeUtils, { CSTagNodeValue } from "utils/taxonomy/csTagNodeUtils";
import taxonomyNodeUtils, { TaxonomyNodeValue } from "utils/taxonomy/taxonomyNodeUtils";

import { partition } from "utils/base";

export const encodeTrendingTagValue = csTagNodeUtils.encodeTrendingTagValue;

/**
 * Either a CSTagNodeValue or TaxonomyNodeValue.
 * Both are strings, but each encodes path data particular to that type of node.
 */
type NodeValue = CSTagNodeValue | TaxonomyNodeValue;

/**
 * Helper function to map a tree of `TaxonomyNodeApi` nodes to a tree of `TaxonomyNode` nodes.
 */
const mapFromApi = (
  nodes: TaxonomyNodeApi[],
  mapFn: (n: TaxonomyNodeApi, parent?: TaxonomyNode) => Omit<TaxonomyNode, "children">,
  parent?: TaxonomyNode,
): TaxonomyNode[] => {
  return nodes.map((node) => {
    const { nodes } = node;
    const newNode = mapFn(node, parent);
    return {
      ...newNode,
      children: nodes && nodes.length > 0 ? mapFromApi(nodes, mapFn, newNode) : undefined,
    };
  });
};

/**
 * Maps the API response to the shape needed by the client app.
 * This expects the caller to separate out the taxonomy nodes from the
 * current account tags & cs tags as we will process them differently.
 *
 * TODO: Determine naming pattern for regular tags. Remove `cs` prefix from
 * all underlying code? Including `csTagNodeUtils.ts`
 */
export const mapToTaxonomyNodes = (
  taxonomyApiNodes: TaxonomyNodeApi[],
  tagApiNodes: TaxonomyNodeApi[],
): TaxonomyNode[] => {
  // The current account tags is at index 0 if theres more than 1 tagApiNodes.
  // The last tag is always Civicscience Tags.
  const currentAccountTags = tagApiNodes.length > 1 ? [tagApiNodes[0]] : [];
  const csTags = tagApiNodes.slice(-1);

  return [
    ...mapFromApi(taxonomyApiNodes, (n, parent) => ({
      value: taxonomyNodeUtils.encodeValue(parent, n),
      label: n.name,
      showCheckbox: !!n.taxonomyNodeId,
    })),
    ...mapFromApi(currentAccountTags, (n, parent) => ({
      value: csTagNodeUtils.encodeValue(parent, n),
      label: n.name,
      showCheckbox: !!n.taxonomyNodeId,
    })),
    ...mapFromApi(csTags, (n, parent) => ({
      value: csTagNodeUtils.encodeValue(parent, n, true),
      label: n.name,
      showCheckbox: !!n.taxonomyNodeId,
    })),
  ];
};

/**
 * Translates the `NodeValue[]` into an array of unique taxonomy tag IDs.
 * We exclude any "IDs" that are NOT numbers.
 * This accounts for the top 2 levels of the hierarchy which are not actually taxonomy nodes
 * and do not have valid IDs in the system. They merely exist as organization for the tree,
 * but are not used to tag content itself.
 *
 * An example NodeValue of `vertical.civic topics.3139` will return `[3139]`
 */
export const buildApiValues = (selectedTags: NodeValue[]) => {
  const [csTags, taxonomyTags] = partition(selectedTags, csTagNodeUtils.isTagNodeValue);

  return {
    taxonomyTags: taxonomyNodeUtils.buildApiValues(taxonomyTags),
    tags: csTagNodeUtils.buildApiValues(csTags),
  };
};

/**
 * Returns the top level taxonomy nodes that are typically initially opened.
 */
export const getInitialExpanded = () => ["verticals", "consumer profile"];
